import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const PatientSegmentation = () => {
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState({
    treatmentType: [],
    ageRange: '',
    lastVisit: '',
    communicationPreference: []
  });

  const segments = [
    {
      id: 'seg1',
      name: 'Upcoming Appointments',
      description: 'Patients with appointments in the next 7 days',
      count: 45,
      icon: 'Calendar',
      color: 'text-primary'
    },
    {
      id: 'seg2',
      name: 'Post-Treatment Follow-Up',
      description: 'Patients who completed treatment in the last 30 days',
      count: 32,
      icon: 'Activity',
      color: 'text-success'
    },
    {
      id: 'seg3',
      name: 'Overdue Recall',
      description: 'Patients overdue for routine checkup (6+ months)',
      count: 78,
      icon: 'AlertCircle',
      color: 'text-warning'
    },
    {
      id: 'seg4',
      name: 'Active Treatment Plans',
      description: 'Patients with ongoing multi-phase treatment',
      count: 23,
      icon: 'GitBranch',
      color: 'text-primary'
    },
    {
      id: 'seg5',
      name: 'Medication Reminders',
      description: 'Patients on prescribed medication regimens',
      count: 15,
      icon: 'Pill',
      color: 'text-error'
    },
    {
      id: 'seg6',
      name: 'New Patients',
      description: 'Patients registered in the last 90 days',
      count: 56,
      icon: 'UserPlus',
      color: 'text-success'
    }
  ];

  const treatmentTypeOptions = [
    { value: 'orthodontics', label: 'Orthodontics' },
    { value: 'implants', label: 'Dental Implants' },
    { value: 'root_canal', label: 'Root Canal' },
    { value: 'crowns', label: 'Crowns & Bridges' },
    { value: 'cleaning', label: 'Cleaning & Prevention' },
    { value: 'cosmetic', label: 'Cosmetic Dentistry' }
  ];

  const ageRangeOptions = [
    { value: '0-17', label: '0-17 years' },
    { value: '18-35', label: '18-35 years' },
    { value: '36-55', label: '36-55 years' },
    { value: '56+', label: '56+ years' }
  ];

  const lastVisitOptions = [
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '180', label: 'Last 6 months' },
    { value: '365', label: 'Last year' },
    { value: '365+', label: 'Over 1 year ago' }
  ];

  const communicationPreferenceOptions = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'voice', label: 'Voice Call' }
  ];

  const handleSegmentToggle = (segmentId) => {
    setSelectedSegments(prev => 
      prev?.includes(segmentId) 
        ? prev?.filter(id => id !== segmentId)
        : [...prev, segmentId]
    );
  };

  const handleCreateCustomSegment = () => {
    console.log('Creating custom segment with criteria:', filterCriteria);
  };

  const totalSelectedPatients = segments
    ?.filter(seg => selectedSegments?.includes(seg?.id))
    ?.reduce((sum, seg) => sum + seg?.count, 0);

  return (
    <div className="space-y-6">
      {/* Pre-defined Segments */}
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="Users" size={20} className="text-primary" />
            Patient Segments
          </h2>
          <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
            Create Custom Segment
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments?.map((segment) => {
            const isSelected = selectedSegments?.includes(segment?.id);
            return (
              <div
                key={segment?.id}
                onClick={() => handleSegmentToggle(segment?.id)}
                className={`bg-muted/30 rounded-lg p-4 border-2 cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Icon name={segment?.icon} size={24} className={segment?.color} />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'bg-primary border-primary' : 'border-border'
                  }`}>
                    {isSelected && <Icon name="Check" size={14} className="text-white" />}
                  </div>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{segment?.name}</h3>
                <p className="text-xs text-muted-foreground mb-3">{segment?.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-foreground">{segment?.count}</span>
                  <span className="text-xs text-muted-foreground">patients</span>
                </div>
              </div>
            );
          })}
        </div>

        {selectedSegments?.length > 0 && (
          <div className="mt-6 bg-primary/10 border border-primary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Selected Segments</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedSegments?.length} segment(s) • {totalSelectedPatients} total patients
                </p>
              </div>
              <Button variant="default" size="sm" iconName="Send" iconPosition="left">
                Send to Selected
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Segment Builder */}
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2 mb-6">
          <Icon name="Filter" size={20} className="text-primary" />
          Custom Segment Builder
        </h2>

        <div className="space-y-4">
          <Select
            label="Treatment Type"
            options={treatmentTypeOptions}
            value={filterCriteria?.treatmentType}
            onChange={(value) => setFilterCriteria({ ...filterCriteria, treatmentType: value })}
            placeholder="Filter by treatment type"
            multiple
            searchable
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Age Range"
              options={ageRangeOptions}
              value={filterCriteria?.ageRange}
              onChange={(value) => setFilterCriteria({ ...filterCriteria, ageRange: value })}
              placeholder="Select age range"
            />
            <Select
              label="Last Visit"
              options={lastVisitOptions}
              value={filterCriteria?.lastVisit}
              onChange={(value) => setFilterCriteria({ ...filterCriteria, lastVisit: value })}
              placeholder="Select timeframe"
            />
          </div>

          <Select
            label="Communication Preference"
            options={communicationPreferenceOptions}
            value={filterCriteria?.communicationPreference}
            onChange={(value) => setFilterCriteria({ ...filterCriteria, communicationPreference: value })}
            placeholder="Filter by preferred channel"
            multiple
          />

          <div className="flex items-center gap-2 pt-4">
            <Button
              variant="default"
              size="sm"
              iconName="Search"
              iconPosition="left"
              onClick={handleCreateCustomSegment}
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={() => setFilterCriteria({ treatmentType: [], ageRange: '', lastVisit: '', communicationPreference: [] })}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientSegmentation;